import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faFileAlt, faMoneyBillWave, faCoins,
  faPercent, faHandHoldingUsd, faChartLine, faChartPie, faChartBar,
  faBuilding, faFilter, faGift, faUserShield, faUsers, faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { UiPageHeader, UiStatsGrid, UiFilterPanel, UiSelect } from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import { fmtMoney } from '../../utils/formatMoney';
import P2pReportPanel, { P2pReportSummary } from '../../components/P2pReportPanel';

const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

const THAI_DAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

interface MonthlyReportDay {
  date: string;
  total_orders: number;
  paid_orders: number;
  pending_orders: number;
  expired_orders: number;
  base_revenue: number;
  total_revenue: number;
  cent_difference: number;
  total_fee: number;
  net_revenue: number;
  success_rate: number;
  payout_count: number;
  payout_amount: number;
  payout_fee: number;
  payin_commission_count: number;
  payin_commission_amount: number;
  payout_commission_count: number;
  payout_commission_amount: number;
}

const toNumber = (value: unknown) => Number(value || 0);

const createLinePath = (points: Array<{ x: number; y: number }>) =>
  points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

const getSuccessRateTextColor = (rate: number) => {
  if (rate >= 80) return 'text-green-600 dark:text-green-400';
  if (rate >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-500 dark:text-red-400';
};

const getSuccessRateStrokeColor = (rate: number) => {
  if (rate >= 80) return '#10b981';
  if (rate >= 50) return '#f59e0b';
  return '#ef4444';
};

const getRevenueBandRange = (value: number, maxValue: number) => {
  if (maxValue <= 0) return { min: 0, max: 0 };
  const steps = 4;
  const bandSize = maxValue / steps;
  if (value <= 0) return { min: 0, max: bandSize };
  const bandIndex = Math.min(steps - 1, Math.floor((value - Number.EPSILON) / bandSize));
  return { min: bandIndex * bandSize, max: (bandIndex + 1) * bandSize };
};

interface SubSpreadSummary {
  total: { payin_amount: number; payout_amount: number; payin_count: number; payout_count: number; total_amount: number };
  by_sub: Array<{
    sub_reseller: { id: string; name: string; email: string; fee_rate: number; payout_fee_rate: number; payin_spread: number; payout_spread: number };
    payin_amount: number; payin_count: number; payout_amount: number; payout_count: number; total_amount: number;
  }>;
  by_date?: Record<string, { payin: number; payout: number; total: number }>;
}

const emptySubSpread: SubSpreadSummary = {
  total: { payin_amount: 0, payout_amount: 0, payin_count: 0, payout_count: 0, total_amount: 0 },
  by_sub: []
};

const ResellerMonthlyReportsPage: React.FC = () => {
  const now = new Date(new Date().getTime() + 7 * 60 * 60 * 1000);
  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');
  const isSubReseller = !!resellerUser?.parent_reseller_id;

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [merchantId, setMerchantId] = useState('');
  const [merchants, setMerchants] = useState<any[]>([]);
  const [report, setReport] = useState<any>({ days: [], summary: {} });
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);
  const [subSpread, setSubSpread] = useState<SubSpreadSummary>(emptySubSpread);
  const [subByDate, setSubByDate] = useState<Record<string, { payin: number; payout: number; total: number }>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeRevenueTooltipIndex, setActiveRevenueTooltipIndex] = useState<number | null>(null);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      let url = `/reseller/reports/monthly?year=${year}&month=${month}`;
      if (merchantId) url += `&merchant_id=${merchantId}`;

      // report = ตัวหลัก (fail = เคลียร์ของเก่า) · sub-commissions = เสริม ให้ fail เงียบ ๆ
      const [reportRes, subSummaryRes] = await Promise.all([
        merchantApi.get(url),
        isSubReseller
          ? Promise.resolve(null)
          : merchantApi.get(`/reseller/sub-commissions/summary?start_date=${startDate}&end_date=${endDate}`).catch(() => null)
      ]);
      setReport(reportRes.data.data);
      if (reportRes.data.data.merchants) setMerchants(reportRes.data.data.merchants);

      // 💧 P2P เดือนนี้ (แยก) ของร้านในสังกัด
      const pFrom = new Date(`${startDate}T00:00:00`).toISOString();
      const pTo = new Date(`${endDate}T23:59:59`).toISOString();
      const p2pRes = await merchantApi.get('/reseller/p2p-report', { params: { date_from: pFrom, date_to: pTo } }).catch(() => null);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);

      if (subSummaryRes) {
        const summaryData = subSummaryRes.data.data;
        setSubSpread(summaryData || emptySubSpread);
        setSubByDate(summaryData?.by_date || {});
      } else {
        setSubSpread(emptySubSpread);
        setSubByDate({});
      }
    } catch (error: any) {
      // เคลียร์ข้อมูลเก่า ไม่ให้ยอดเดือนก่อนค้างบนจอ
      setReport({ days: [], summary: {} });
      setSubSpread(emptySubSpread);
      setSubByDate({});
      setP2p(null);
      setLoadError(error.response?.data?.message || error.message || 'โหลดรายงานไม่สำเร็จ');
      toast.error('ไม่สามารถโหลดรายงานได้: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [year, month, merchantId, isSubReseller]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatCurrency = (val: number) =>
    `฿${toNumber(val).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`;
  const formatAxisCurrency = (val: number) =>
    `฿${toNumber(val).toLocaleString('th-TH', { maximumFractionDigits: 0 })}`;
  const formatApproxRevenueRange = (value: number) => {
    const range = getRevenueBandRange(value, maxRevenueValue);
    return `${formatAxisCurrency(range.min)} - ${formatAxisCurrency(range.max)}`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const dayName = THAI_DAYS[d.getDay()];
    return `${dayName}. ${day}`;
  };

  const yearOptions = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);

  const columns: Column<any>[] = [
    {
      key: 'date',
      header: 'วันที่',
      render: (d) => (
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
            style={{ background: 'var(--theme-primary-light)', color: 'var(--theme-primary)' }}
          >
            {new Date(d.date).getDate()}
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{formatDate(d.date)}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{d.date}</p>
          </div>
        </div>
      )
    },
    {
      key: 'total_orders',
      header: 'Orders',
      sortable: true,
      render: (d) => d.total_orders > 0
        ? <span className="font-bold text-gray-800 dark:text-slate-100">{d.total_orders}</span>
        : <span className="text-gray-300 dark:text-slate-600">-</span>
    },
    {
      key: 'paid_orders',
      header: 'Payin',
      sortable: true,
      render: (d) => (
        <div>
          {(d.paid_orders || 0) > 0 ? (
            <>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{d.paid_orders} รายการ</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500 tabular-nums">฿{parseFloat(d.base_revenue || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-amber-600 dark:text-amber-400 tabular-nums">Fee: ฿{parseFloat(d.total_fee || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'payin_commission_amount',
      header: 'Com. Payin',
      sortable: true,
      render: (d: any) => {
        const sub = !isSubReseller ? (subByDate[d.date]?.payin || 0) : 0;
        const total = (d.payin_commission_amount || 0) + sub;
        if (total <= 0 && (d.payin_commission_count || 0) === 0) return <span className="text-gray-300 dark:text-slate-600">-</span>;
        return (
          <div>
            <span className="font-semibold text-green-600 dark:text-green-400 tabular-nums">+฿{fmtMoney(total)}</span>
            <div className="text-[11px] text-gray-400 dark:text-slate-500">{d.payin_commission_count} รายการ</div>
            {sub > 0 && <div className="text-[11px] text-amber-500 dark:text-amber-400 tabular-nums">+฿{fmtMoney(sub)} Sub</div>}
          </div>
        );
      }
    },
    {
      key: 'payout_count',
      header: 'Payout',
      sortable: true,
      render: (d) => (
        <div>
          {(d.payout_count || 0) > 0 ? (
            <>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">{d.payout_count} รายการ</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500 tabular-nums">฿{parseFloat(d.payout_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-amber-600 dark:text-amber-400 tabular-nums">Fee: ฿{parseFloat(d.payout_fee || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      )
    },
    {
      key: 'payout_commission_amount',
      header: 'Com. Payout',
      sortable: true,
      render: (d: any) => {
        const sub = !isSubReseller ? (subByDate[d.date]?.payout || 0) : 0;
        const total = (d.payout_commission_amount || 0) + sub;
        if (total <= 0 && (d.payout_commission_count || 0) === 0) return <span className="text-gray-300 dark:text-slate-600">-</span>;
        return (
          <div>
            <span className="font-semibold tabular-nums" style={{ color: 'var(--theme-accent)' }}>+฿{fmtMoney(total)}</span>
            <div className="text-[11px] text-gray-400 dark:text-slate-500">{d.payout_commission_count} รายการ</div>
            {sub > 0 && <div className="text-[11px] text-amber-500 dark:text-amber-400 tabular-nums">+฿{fmtMoney(sub)} Sub</div>}
          </div>
        );
      }
    },
    {
      key: 'total_commission',
      header: 'Comm. รวม',
      sortable: true,
      render: (d: any) => {
        const sub = !isSubReseller ? (subByDate[d.date]?.total || 0) : 0;
        const total = (d.payin_commission_amount || 0) + (d.payout_commission_amount || 0) + sub;
        if (total <= 0) return <span className="text-gray-300 dark:text-slate-600">-</span>;
        return (
          <div>
            <span className="font-bold tabular-nums" style={{ color: 'var(--theme-primary)' }}>+฿{fmtMoney(total)}</span>
            {sub > 0 && <div className="text-[11px] text-amber-500 dark:text-amber-400 font-medium tabular-nums">Sub ฿{fmtMoney(sub)}</div>}
          </div>
        );
      }
    },
    {
      key: 'net_revenue',
      header: 'ยอดสุทธิ',
      sortable: true,
      render: (d) => (
        <span className="font-bold text-green-600 dark:text-green-400 text-base tabular-nums">
          {formatCurrency(d.net_revenue)}
        </span>
      )
    },
    {
      key: 'success_rate',
      header: 'Success %',
      sortable: true,
      render: (d) => {
        if (d.total_orders === 0) return <span className="text-gray-300 dark:text-slate-600">-</span>;
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${d.success_rate}%`,
                  background: d.success_rate >= 80 ? 'linear-gradient(to right, #10b981, #34d399)' :
                    d.success_rate >= 50 ? 'linear-gradient(to right, #f59e0b, #fbbf24)' :
                      'linear-gradient(to right, #ef4444, #f87171)'
                }}
              />
            </div>
            <span className={`text-xs font-bold min-w-[40px] text-right ${getSuccessRateTextColor(d.success_rate)}`}>
              {parseFloat(d.success_rate).toFixed(1)}%
            </span>
          </div>
        );
      }
    }
  ];

  const summary = report.summary || {};
  const reportDays: MonthlyReportDay[] = (report.days || []).map((day: any) => ({
    date: day.date,
    total_orders: toNumber(day.total_orders),
    paid_orders: toNumber(day.paid_orders),
    pending_orders: toNumber(day.pending_orders),
    expired_orders: toNumber(day.expired_orders),
    base_revenue: toNumber(day.base_revenue),
    total_revenue: toNumber(day.total_revenue),
    cent_difference: toNumber(day.cent_difference),
    total_fee: toNumber(day.total_fee),
    net_revenue: toNumber(day.net_revenue),
    success_rate: toNumber(day.success_rate),
    payout_count: toNumber(day.payout_count),
    payout_amount: toNumber(day.payout_amount),
    payout_fee: toNumber(day.payout_fee),
    payin_commission_count: toNumber(day.payin_commission_count),
    payin_commission_amount: toNumber(day.payin_commission_amount),
    payout_commission_count: toNumber(day.payout_commission_count),
    payout_commission_amount: toNumber(day.payout_commission_amount),
  }));

  // เสริม sub-reseller spread เข้าไปในแต่ละวัน
  const existingDates = new Set(reportDays.map(d => d.date));
  const emptyDay = (date: string) => ({
    date, total_orders: 0, paid_orders: 0, pending_orders: 0, expired_orders: 0,
    base_revenue: 0, total_revenue: 0, cent_difference: 0, total_fee: 0,
    net_revenue: 0, success_rate: 0,
    payout_count: 0, payout_amount: 0, payout_fee: 0,
    payin_commission_count: 0, payin_commission_amount: 0,
    payout_commission_count: 0, payout_commission_amount: 0,
  });

  // วันที่มีแค่ sub spread แต่ไม่มี merchant order → ต้องเพิ่มเข้ามาเอง
  const subOnlyRows = !isSubReseller
    ? Object.keys(subByDate)
        .filter(date => !existingDates.has(date) && subByDate[date].total > 0)
        .map(date => {
          const sub = subByDate[date];
          return {
            ...emptyDay(date),
            sub_payin: sub.payin, sub_payout: sub.payout, sub_total: sub.total,
            total_commission: sub.total,
          };
        })
    : [];

  const enrichedDays = [
    ...reportDays.map(d => {
      const sub = !isSubReseller ? (subByDate[d.date] || { payin: 0, payout: 0, total: 0 }) : { payin: 0, payout: 0, total: 0 };
      return {
        ...d,
        sub_payin: sub.payin,
        sub_payout: sub.payout,
        sub_total: sub.total,
        total_commission: d.payin_commission_amount + d.payout_commission_amount + sub.total,
      };
    }),
    ...subOnlyRows,
  ].sort((a, b) => a.date.localeCompare(b.date));

  const activeDays = enrichedDays.filter((day) => day.total_orders > 0);
  const selectedMerchant = merchantId && merchants.length > 0
    ? merchants.find((m: any) => m.id === merchantId)
    : null;
  const selectedMerchantName = selectedMerchant?.name || selectedMerchant?.company_name || '';

  const maxRevenueValue = Math.max(1, ...enrichedDays.map((day) => Math.max(day.total_revenue, day.net_revenue)));
  const peakRevenueDay = enrichedDays.reduce<typeof enrichedDays[0] | null>(
    (peak, day) => (!peak || day.total_revenue > peak.total_revenue ? day : peak),
    null
  );
  const highestOrdersDay = activeDays.reduce<typeof enrichedDays[0] | null>(
    (peak, day) => (!peak || day.total_orders > peak.total_orders ? day : peak),
    null
  );
  const bestSuccessDay = activeDays.reduce<typeof enrichedDays[0] | null>(
    (best, day) => (!best || day.success_rate > best.success_rate ? day : best),
    null
  );
  const weakestSuccessDay = activeDays.reduce<typeof enrichedDays[0] | null>(
    (worst, day) => (!worst || day.success_rate < worst.success_rate ? day : worst),
    null
  );
  const averageNetRevenue = enrichedDays.length > 0
    ? enrichedDays.reduce((sum, day) => sum + day.net_revenue, 0) / enrichedDays.length
    : 0;
  const averageOrdersPerActiveDay = activeDays.length > 0
    ? activeDays.reduce((sum, day) => sum + day.total_orders, 0) / activeDays.length
    : 0;
  const totalMonthCommission = enrichedDays.reduce((sum, day) => sum + day.total_commission, 0);

  const orderStatusItems = [
    { label: 'Paid', value: enrichedDays.reduce((sum, day) => sum + day.paid_orders, 0), barClass: 'bg-green-500', textClass: 'text-green-600 dark:text-green-400', bgClass: 'bg-green-50 dark:bg-green-900/20' },
    { label: 'Other', value: enrichedDays.reduce((sum, day) => sum + (day.total_orders - day.paid_orders), 0), barClass: 'bg-gray-400 dark:bg-slate-500', textClass: 'text-gray-600 dark:text-slate-300', bgClass: 'bg-gray-50 dark:bg-slate-700/50' },
  ];
  const totalStatusOrders = Math.max(1, orderStatusItems.reduce((sum, item) => sum + item.value, 0));
  const revenueAxisTicks = [1, 0.75, 0.5, 0.25, 0].map((ratio) => ({ ratio, value: maxRevenueValue * ratio }));
  const activeRevenueTooltipDay = activeRevenueTooltipIndex !== null ? enrichedDays[activeRevenueTooltipIndex] || null : null;
  const revenueTooltipLeftPercent = activeRevenueTooltipIndex !== null && enrichedDays.length > 0
    ? Math.min(88, Math.max(12, ((activeRevenueTooltipIndex + 0.5) / enrichedDays.length) * 100))
    : 50;

  // chart ใช้เฉพาะวันที่มี orders จริง เพื่อไม่ให้ sub-only days ดึงเส้นลง 0%
  const chartDays = enrichedDays.filter(d => d.total_orders > 0);
  const successChartWidth = Math.max(620, chartDays.length * 42);
  const successChartHeight = 220;
  const successChartPadding = { top: 18, right: 18, bottom: 30, left: 18 };
  const successChartBottom = successChartHeight - successChartPadding.bottom;
  const successChartInnerWidth = successChartWidth - successChartPadding.left - successChartPadding.right;
  const successChartInnerHeight = successChartHeight - successChartPadding.top - successChartPadding.bottom;
  const successPoints = chartDays.map((day, index) => {
    const x = successChartPadding.left + (
      chartDays.length === 1
        ? successChartInnerWidth / 2
        : (index / Math.max(chartDays.length - 1, 1)) * successChartInnerWidth
    );
    const y = successChartPadding.top + (1 - (day.success_rate / 100)) * successChartInnerHeight;
    return { x, y, day };
  });
  const successLinePath = successPoints.length > 0 ? createLinePath(successPoints) : '';
  const successAreaPath = successPoints.length > 1
    ? `${successLinePath} L ${successPoints[successPoints.length - 1].x} ${successChartBottom} L ${successPoints[0].x} ${successChartBottom} Z`
    : '';
  const successTicks = [0, 25, 50, 75, 100];
  const successLabelStep = chartDays.length > 20 ? 3 : 2;

  const summaryCards: StatCardDef[] = [
    {
      icon: faFileAlt,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'Total Orders',
      value: <span style={{ color: 'var(--theme-primary)' }}>{(summary.total_orders || 0).toLocaleString()}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">ทั้งเดือน</span>
    },
    {
      icon: faMoneyBillWave,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-accent)' },
      label: 'ยอดฝาก (Base)',
      value: <span className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(summary.base_revenue || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">ยอดที่ลูกค้ากรอก</span>
    },
    {
      icon: faCoins,
      iconClassName: 'bg-blue-500 text-white',
      label: 'ยอดรับจริง',
      value: <span className="text-blue-600 dark:text-blue-400 tabular-nums">฿{fmtMoney(summary.total_revenue || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">รวมสตางค์</span>
    },
    {
      icon: faPercent,
      iconClassName: 'bg-red-500 text-white',
      label: 'ค่าธรรมเนียม',
      value: <span className="text-red-600 dark:text-red-400 tabular-nums">-฿{fmtMoney(summary.total_fee || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">ค่าบริการรวม</span>
    },
    {
      icon: faHandHoldingUsd,
      iconClassName: 'bg-green-500 text-white',
      label: 'ยอดสุทธิ',
      value: <span className="text-green-600 dark:text-green-400 tabular-nums">฿{fmtMoney(summary.net_revenue || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">Merchants ได้</span>
    },
    {
      icon: faChartLine,
      iconClassName: 'bg-green-500 text-white',
      label: 'Success Rate',
      value: <span className="text-green-600 dark:text-green-400">{parseFloat(summary.success_rate || 0).toFixed(1)}%</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">อัตราสำเร็จ</span>
    },
  ];

  const payoutCards: StatCardDef[] = [
    {
      icon: faHandHoldingUsd,
      iconClassName: 'bg-indigo-500 text-white',
      label: 'Payout ทั้งเดือน',
      value: <span className="text-indigo-600 dark:text-indigo-400">{(summary.total_payouts || 0).toLocaleString()} รายการ</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500 tabular-nums">ยอดถอนรวม {formatCurrency(summary.total_payout_amount || 0)}</span>
    },
    {
      icon: faPercent,
      iconClassName: 'bg-amber-500 text-white',
      label: 'ค่าธรรมเนียมถอน',
      value: <span className="text-amber-600 dark:text-amber-400 tabular-nums">฿{fmtMoney(summary.total_payout_fee || 0)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">Payout Fee รวม</span>
    },
    {
      icon: faCoins,
      iconClassName: 'bg-purple-500 text-white',
      label: 'Fee รวม (Payin + Payout)',
      value: <span className="text-purple-600 dark:text-purple-400 tabular-nums">฿{fmtMoney((summary.total_fee || 0) + (summary.total_payout_fee || 0))}</span>,
      subValue: (
        <span className="text-gray-400 dark:text-slate-500 tabular-nums">
          Payin ฿{fmtMoney(summary.total_fee || 0)} + Payout ฿{fmtMoney(summary.total_payout_fee || 0)}
        </span>
      )
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faCalendarAlt}
        title="Monthly Reports"
        below={
          <p className="text-white/70 text-sm mt-1 flex items-center gap-2 flex-wrap">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            รายงานรายเดือน สรุปแต่ละวัน - {THAI_MONTHS[month - 1]} {year}
            {selectedMerchantName && <span className="text-white/90 font-semibold"> · {selectedMerchantName}</span>}
          </p>
        }
      />

      {/* Filters — เปลี่ยนค่าแล้วโหลดใหม่อัตโนมัติ · ปุ่มค้นหา = โหลดซ้ำ */}
      <UiFilterPanel onSearch={loadReport} loading={loading}>
        <UiSelect
          label="เดือน"
          wrapperClassName="w-[160px] shrink-0"
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value))}
        >
          {THAI_MONTHS.map((name, i) => (
            <option key={i} value={i + 1}>{name}</option>
          ))}
        </UiSelect>
        <UiSelect
          label="ปี"
          wrapperClassName="w-[120px] shrink-0"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </UiSelect>
        <UiSelect
          label="Merchant"
          icon={faFilter}
          wrapperClassName="w-[240px] shrink-0"
          value={merchantId}
          onChange={(e) => setMerchantId(e.target.value)}
        >
          <option value="">ทุก Merchant</option>
          {merchants.map((m: any) => (
            <option key={m.id} value={m.id}>{m.name || m.company_name}</option>
          ))}
        </UiSelect>
      </UiFilterPanel>

      {loadError && (
        <div className="mb-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-start gap-3">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500 dark:text-red-400 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-red-700 dark:text-red-400">โหลดข้อมูลเดือนนี้ไม่สำเร็จ</p>
            <p className="text-red-600/80 dark:text-red-400/80">ตัวเลขถูกล้างเพื่อไม่ให้สับสนกับเดือนก่อนหน้า — กรุณาลองใหม่อีกครั้ง ({loadError})</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <UiStatsGrid cards={summaryCards} loading={loading} cols={3} className="sm:grid-cols-2 2xl:grid-cols-6" />

      {/* Payout Fee Summary */}
      {(summary.total_payouts > 0 || summary.total_payout_fee > 0) && (
        <UiStatsGrid cards={payoutCards} cols={3} />
      )}

      <P2pReportPanel summary={p2p} />

      {/* Commission Summary */}
      {(() => {
        const merchantComm = summary.total_commission_amount || 0;
        const subTotal = !isSubReseller ? subSpread.total.total_amount : 0;
        const grandTotal = merchantComm + subTotal;
        if (grandTotal <= 0) return null;

        const merchantPayin = summary.payin_commission_amount || 0;
        const merchantPayout = summary.payout_commission_amount || 0;
        const merchantPayinCount = summary.payin_commission_count || 0;
        const merchantPayoutCount = summary.payout_commission_count || 0;
        const totalPayinComm = merchantPayin + subSpread.total.payin_amount;
        const totalPayoutComm = merchantPayout + subSpread.total.payout_amount;
        const totalTxCount = merchantPayinCount + merchantPayoutCount
          + subSpread.total.payin_count + subSpread.total.payout_count;
        const hasSubData = !isSubReseller && subTotal > 0;

        return (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ background: 'var(--theme-primary-gradient)' }}
              >
                <FontAwesomeIcon icon={faGift} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-800 dark:text-slate-100">Commission ที่ได้รับ</h3>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {THAI_MONTHS[month - 1]} {year}
                  {hasSubData && ' · รวม Merchant + Sub-Reseller Spread'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {/* Commission รวม */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border-l-4" style={{ borderLeftColor: 'var(--theme-primary)' }}>
                <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Commission รวม</p>
                <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--theme-primary)' }}>+฿{fmtMoney(grandTotal)}</p>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{totalTxCount.toLocaleString()} รายการ</p>
                {hasSubData && (
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-600 space-y-0.5">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                        <FontAwesomeIcon icon={faUsers} className="text-[9px]" /> Merchant
                      </span>
                      <span className="font-medium tabular-nums" style={{ color: 'var(--theme-primary)' }}>฿{fmtMoney(merchantComm)}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400 dark:text-slate-500 flex items-center gap-1">
                        <FontAwesomeIcon icon={faUserShield} className="text-[9px]" /> Sub-Reseller
                      </span>
                      <span className="text-amber-500 dark:text-amber-400 font-medium tabular-nums">฿{fmtMoney(subTotal)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payin Commission */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border-l-4 border-green-500">
                <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Payin Commission</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400 tabular-nums">+฿{fmtMoney(totalPayinComm)}</p>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  {(merchantPayinCount + subSpread.total.payin_count).toLocaleString()} รายการ
                </p>
                {hasSubData && subSpread.total.payin_amount > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-600 space-y-0.5">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400 dark:text-slate-500">Merchant</span>
                      <span className="text-green-600 dark:text-green-400 font-medium tabular-nums">฿{fmtMoney(merchantPayin)}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400 dark:text-slate-500">Sub Spread</span>
                      <span className="text-amber-500 dark:text-amber-400 font-medium tabular-nums">฿{fmtMoney(subSpread.total.payin_amount)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payout Commission */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border-l-4" style={{ borderLeftColor: 'var(--theme-accent)' }}>
                <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Payout Commission</p>
                <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--theme-accent)' }}>+฿{fmtMoney(totalPayoutComm)}</p>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  {(merchantPayoutCount + subSpread.total.payout_count).toLocaleString()} รายการ
                </p>
                {hasSubData && subSpread.total.payout_amount > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-600 space-y-0.5">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400 dark:text-slate-500">Merchant</span>
                      <span className="font-medium tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(merchantPayout)}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400 dark:text-slate-500">Sub Spread</span>
                      <span className="text-amber-500 dark:text-amber-400 font-medium tabular-nums">฿{fmtMoney(subSpread.total.payout_amount)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* เฉลี่ย/รายการ */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border-l-4 border-gray-300 dark:border-slate-500">
                <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">เฉลี่ย/รายการ</p>
                <p className="text-xl font-bold text-gray-700 dark:text-slate-200 tabular-nums">
                  ฿{fmtMoney(totalTxCount > 0 ? grandTotal / totalTxCount : 0)}
                </p>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ต่อรายการ (รวมทุกแหล่ง)</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        {/* Revenue Bar Chart */}
        <div className="xl:col-span-2 bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-blue-500 text-xs" />
                กราฟยอดรับรายวัน
              </h2>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">เปรียบเทียบยอดรับจริงกับยอดสุทธิของแต่ละวันในเดือนนี้</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-blue-600 to-cyan-400"></span>
                ยอดรับจริง
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-green-600 to-green-400"></span>
                ยอดสุทธิ
              </span>
            </div>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="flex h-64 items-end gap-2 px-2">
                {Array.from({ length: 16 }).map((_, index) => (
                  <div key={index} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                    <div className="flex h-full items-end gap-1">
                      <div className="w-3 rounded-t-lg bg-gray-100 dark:bg-slate-700 animate-pulse" style={{ height: `${30 + (index % 5) * 10}%` }}></div>
                      <div className="w-3 rounded-t-lg bg-gray-200 dark:bg-slate-600 animate-pulse" style={{ height: `${22 + (index % 4) * 9}%` }}></div>
                    </div>
                    <div className="h-3 w-5 rounded bg-gray-100 dark:bg-slate-700 animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : reportDays.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400 dark:text-slate-500">
                <FontAwesomeIcon icon={faChartLine} className="text-4xl text-gray-200 dark:text-slate-600 mb-3" />
                <p className="text-sm font-medium">ยังไม่มีข้อมูลกราฟของเดือนนี้</p>
                <p className="text-xs mt-1">ลองเปลี่ยนเดือนหรือเลือก Merchant อื่นดูได้</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto pb-2">
                  <div className="relative min-w-[840px] pt-24">
                    {activeRevenueTooltipDay && (
                      <div className="pointer-events-none absolute left-0 right-0 top-0 z-30 h-20">
                        <div
                          className="absolute min-w-[230px] max-w-[260px] -translate-x-1/2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-900/95 px-3 py-2 text-xs text-white shadow-2xl backdrop-blur-sm"
                          style={{ left: `${revenueTooltipLeftPercent}%` }}
                        >
                          <div className="border-b border-white/10 pb-2">
                            <p className="font-semibold text-white">{formatDate(activeRevenueTooltipDay.date)}</p>
                            <p className="mt-0.5 text-[11px] text-slate-300">{activeRevenueTooltipDay.date}</p>
                          </div>
                          <div className="space-y-2 pt-2">
                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-1.5 text-slate-300">
                                  <span className="h-2.5 w-2.5 rounded-full bg-cyan-400"></span>
                                  ยอดรับจริง
                                </span>
                                <span className="font-semibold text-cyan-300 tabular-nums">{formatCurrency(activeRevenueTooltipDay.total_revenue)}</span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-400">
                                ช่วงประมาณ: {formatApproxRevenueRange(activeRevenueTooltipDay.total_revenue)}
                              </p>
                            </div>
                            <div>
                              <div className="flex items-center justify-between gap-3">
                                <span className="inline-flex items-center gap-1.5 text-slate-300">
                                  <span className="h-2.5 w-2.5 rounded-full bg-green-400"></span>
                                  ยอดสุทธิ
                                </span>
                                <span className="font-semibold text-green-300 tabular-nums">{formatCurrency(activeRevenueTooltipDay.net_revenue)}</span>
                              </div>
                              <p className="mt-1 text-[11px] text-slate-400">
                                ช่วงประมาณ: {formatApproxRevenueRange(activeRevenueTooltipDay.net_revenue)}
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 border-t border-white/10 pt-2 text-[11px] text-slate-300">
                              <span>Orders: <strong className="text-white">{activeRevenueTooltipDay.total_orders.toLocaleString()}</strong></span>
                              <span>Success: <strong className="text-white">{activeRevenueTooltipDay.success_rate.toFixed(1)}%</strong></span>
                              <span className="col-span-2 flex items-center justify-between">
                                <span>Commission รวม:</span>
                                <strong className="text-green-300 tabular-nums">
                                  +฿{fmtMoney((activeRevenueTooltipDay as any).total_commission || 0)}
                                  {(activeRevenueTooltipDay as any).sub_total > 0 && (
                                    <span className="text-amber-300 ml-1">(Sub +฿{fmtMoney((activeRevenueTooltipDay as any).sub_total)})</span>
                                  )}
                                </strong>
                              </span>
                            </div>
                          </div>
                          <div className="absolute left-1/2 top-full h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-slate-200 dark:border-slate-600 bg-slate-900/95"></div>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3" onMouseLeave={() => setActiveRevenueTooltipIndex(null)}>
                      <div className="w-20 sm:w-24 flex-shrink-0">
                        <div className="flex h-56 flex-col justify-between pr-2 text-right text-[10px] font-medium text-gray-400 dark:text-slate-500">
                          {revenueAxisTicks.map((tick) => (
                            <span key={tick.ratio} className="whitespace-nowrap">
                              {formatAxisCurrency(tick.value)}
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 pr-2 text-right text-[10px] font-medium text-gray-300 dark:text-slate-600">ช่วงราคา</div>
                      </div>

                      <div className="flex-1">
                        <div className="relative h-56">
                          <div className="pointer-events-none absolute inset-0">
                            {revenueAxisTicks.map((tick) => (
                              <div
                                key={tick.ratio}
                                className="absolute left-0 right-0 border-t border-dashed border-gray-200 dark:border-slate-600"
                                style={{ top: `${(1 - tick.ratio) * 100}%` }}
                              ></div>
                            ))}
                          </div>

                          <div className="relative z-10 flex h-full items-end gap-2 px-1">
                            {enrichedDays.map((day, index) => {
                              const totalHeight = day.total_revenue > 0
                                ? Math.max((day.total_revenue / maxRevenueValue) * 100, 5) : 0;
                              const netHeight = day.net_revenue > 0
                                ? Math.max((day.net_revenue / maxRevenueValue) * 100, 5) : 0;
                              const isPeak = peakRevenueDay?.date === day.date && day.total_revenue > 0;

                              return (
                                <button
                                  key={day.date}
                                  type="button"
                                  onMouseEnter={() => setActiveRevenueTooltipIndex(index)}
                                  onFocus={() => setActiveRevenueTooltipIndex(index)}
                                  onClick={() => setActiveRevenueTooltipIndex((current) => current === index ? null : index)}
                                  className="relative flex h-full min-w-[18px] flex-1 items-end justify-center gap-1 bg-transparent focus:outline-none"
                                >
                                  <div
                                    className={`w-3 rounded-t-md bg-gradient-to-t from-blue-600 to-cyan-400 transition-all duration-300 hover:opacity-90 ${isPeak ? 'ring-2 ring-blue-200 dark:ring-blue-500/40 ring-offset-1 dark:ring-offset-slate-800' : ''}`}
                                    style={{ height: `${totalHeight}%`, minHeight: day.total_revenue > 0 ? '6px' : '0px' }}
                                  ></div>
                                  <div
                                    className="w-3 rounded-t-md bg-gradient-to-t from-green-600 to-green-400 transition-all duration-300 hover:opacity-90"
                                    style={{ height: `${netHeight}%`, minHeight: day.net_revenue > 0 ? '6px' : '0px' }}
                                  ></div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="mt-2 flex gap-2 px-1">
                          {enrichedDays.map((day) => {
                            const isPeak = peakRevenueDay?.date === day.date && day.total_revenue > 0;
                            return (
                              <div key={day.date} className="min-w-[18px] flex-1 text-center">
                                <span className={`text-[10px] ${isPeak ? 'font-bold text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}>
                                  {new Date(day.date).getDate()}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
                    <p className="text-[11px] font-medium text-blue-500 dark:text-blue-400 uppercase">Peak Revenue</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                      {peakRevenueDay ? formatDate(peakRevenueDay.date) : '-'}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 tabular-nums">
                      {peakRevenueDay ? formatCurrency(peakRevenueDay.total_revenue) : 'ไม่มีข้อมูล'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-green-50 dark:bg-green-900/20 p-3">
                    <p className="text-[11px] font-medium text-green-600 dark:text-green-400 uppercase">Average Net / Day</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1 tabular-nums">{formatCurrency(averageNetRevenue)}</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      เฉลี่ย {enrichedDays.length.toLocaleString()} วันในรายงาน
                    </p>
                  </div>
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
                    <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400 uppercase">Average Orders / Active Day</p>
                    <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">{averageOrdersPerActiveDay.toFixed(1)} Orders</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      วันที่มีรายการ {activeDays.length.toLocaleString()} วัน
                    </p>
                  </div>
                  <div className="rounded-xl p-3" style={{ background: 'var(--theme-primary-light)' }}>
                    <p className="text-[11px] font-medium uppercase" style={{ color: 'var(--theme-primary)' }}>
                      Commission{!isSubReseller && subSpread.total.total_amount > 0 ? ' (+ Sub)' : ''}
                    </p>
                    <p className="text-sm font-bold mt-1 tabular-nums" style={{ color: 'var(--theme-primary)' }}>+{formatCurrency(totalMonthCommission)}</p>
                    {!isSubReseller && subSpread.total.total_amount > 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 tabular-nums">Sub ฿{fmtMoney(subSpread.total.total_amount)}</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Order Status Pie */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faChartPie} className="text-xs text-primary" />
              ภาพรวมสถานะ Orders
            </h2>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">สัดส่วน Paid, Pending และ Expired ทั้งเดือน</p>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="space-y-3">
                <div className="h-24 rounded-2xl bg-gray-100 dark:bg-slate-700 animate-pulse"></div>
                <div className="h-3 rounded-full bg-gray-100 dark:bg-slate-700 animate-pulse"></div>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-12 rounded-xl bg-gray-100 dark:bg-slate-700 animate-pulse"></div>
                ))}
              </div>
            ) : reportDays.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400 dark:text-slate-500">
                <FontAwesomeIcon icon={faChartPie} className="text-4xl text-gray-200 dark:text-slate-600 mb-3" />
                <p className="text-sm font-medium">ไม่มีข้อมูลสำหรับสรุปสถานะ</p>
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-green-100 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 p-4">
                  <p className="text-[11px] font-medium text-green-600 dark:text-green-400 uppercase">Monthly Success Rate</p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className={`text-3xl font-bold ${getSuccessRateTextColor(toNumber(summary.success_rate))}`}>
                      {toNumber(summary.success_rate).toFixed(1)}%
                    </span>
                    <span className="text-sm text-gray-400 dark:text-slate-500">เฉลี่ยทั้งเดือน</span>
                  </div>
                  {selectedMerchantName && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                      Merchant: <span className="font-medium text-gray-700 dark:text-slate-200">{selectedMerchantName}</span>
                    </p>
                  )}
                </div>

                <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700 flex">
                  {orderStatusItems.map((item) => (
                    <div
                      key={item.label}
                      className={`${item.barClass} transition-all duration-500`}
                      style={{ width: `${(item.value / totalStatusOrders) * 100}%` }}
                    ></div>
                  ))}
                </div>

                <div className="mt-4 space-y-2">
                  {orderStatusItems.map((item) => (
                    <div key={item.label} className={`flex items-center gap-2 rounded-xl px-3 py-2 ${item.bgClass}`}>
                      <span className={`h-3 w-3 rounded-full ${item.barClass}`}></span>
                      <span className="text-sm text-gray-600 dark:text-slate-300">{item.label}</span>
                      <span className={`ml-auto text-sm font-bold ${item.textClass}`}>
                        {item.value.toLocaleString()} ({((item.value / totalStatusOrders) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2">
                  <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-400 dark:text-slate-500">Highest Orders Day</p>
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mt-1">
                      {highestOrdersDay ? `${formatDate(highestOrdersDay.date)} • ${highestOrdersDay.total_orders.toLocaleString()} Orders` : 'ไม่มีข้อมูล'}
                    </p>
                  </div>
                  <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 px-3 py-2">
                    <p className="text-[11px] uppercase text-gray-400 dark:text-slate-500">Days With Activity</p>
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mt-1">{activeDays.length.toLocaleString()} วัน</p>
                  </div>
                  <div className="rounded-xl px-3 py-2" style={{ background: 'var(--theme-primary-light)' }}>
                    <p className="text-[11px] uppercase" style={{ color: 'var(--theme-primary)' }}>Commission รวมทั้งเดือน</p>
                    <p className="text-sm font-bold mt-1 tabular-nums" style={{ color: 'var(--theme-primary)' }}>+{formatCurrency(totalMonthCommission)}</p>
                    {!isSubReseller && subSpread.total.total_amount > 0 && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5 tabular-nums">Sub Spread ฿{fmtMoney(subSpread.total.total_amount)}</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success Rate Trend */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2">
              <FontAwesomeIcon icon={faChartBar} className="text-amber-500 dark:text-amber-400 text-xs" />
              Success Rate Trend
            </h2>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">เส้นแนวโน้มอัตราสำเร็จรายวันของเดือนนี้</p>
          </div>
          <span className={`inline-flex self-start rounded-full px-3 py-1 text-xs font-semibold ${getSuccessRateTextColor(toNumber(summary.success_rate))} bg-white dark:bg-slate-700`}>
            Average {toNumber(summary.success_rate).toFixed(1)}%
          </span>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="space-y-4">
              <div className="h-60 rounded-2xl bg-gray-100 dark:bg-slate-700 animate-pulse"></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-20 rounded-xl bg-gray-100 dark:bg-slate-700 animate-pulse"></div>
                ))}
              </div>
            </div>
          ) : reportDays.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-center text-gray-400 dark:text-slate-500">
              <FontAwesomeIcon icon={faChartLine} className="text-4xl text-gray-200 dark:text-slate-600 mb-3" />
              <p className="text-sm font-medium">ไม่มีข้อมูลสำหรับแสดงเส้นแนวโน้ม</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <svg
                  viewBox={`0 0 ${successChartWidth} ${successChartHeight}`}
                  className="h-60 w-full min-w-[620px]"
                  role="img"
                  aria-label="Monthly success rate trend"
                >
                  <defs>
                    <linearGradient id="resellerSuccessStroke" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#f59e0b" />
                      <stop offset="50%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                    <linearGradient id="resellerSuccessArea" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="rgba(16,185,129,0.18)" />
                      <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                    </linearGradient>
                  </defs>

                  {successTicks.map((tick) => {
                    const y = successChartPadding.top + (1 - (tick / 100)) * successChartInnerHeight;
                    return (
                      <g key={tick}>
                        <line
                          x1={successChartPadding.left}
                          y1={y}
                          x2={successChartWidth - successChartPadding.right}
                          y2={y}
                          stroke="currentColor"
                          className="text-gray-200 dark:text-slate-600"
                          strokeDasharray="4 4"
                        />
                        <text x={0} y={y + 4} fontSize="10" fill="#94a3b8">{tick}%</text>
                      </g>
                    );
                  })}

                  {successAreaPath && <path d={successAreaPath} fill="url(#resellerSuccessArea)" />}
                  {successLinePath && (
                    <path d={successLinePath} fill="none" stroke="url(#resellerSuccessStroke)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  )}

                  {successPoints.map((point, index) => (
                    <g key={point.day.date}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="4.5"
                        fill="white"
                        className="dark:fill-slate-800"
                        stroke={getSuccessRateStrokeColor(point.day.success_rate)}
                        strokeWidth="3"
                      >
                        <title>{`${formatDate(point.day.date)} • ${point.day.success_rate.toFixed(1)}% • Paid ${point.day.paid_orders}/${point.day.total_orders}`}</title>
                      </circle>
                      {(index % successLabelStep === 0 || index === successPoints.length - 1) && (
                        <text x={point.x} y={successChartBottom + 18} fontSize="10" fill="#94a3b8" textAnchor="middle">
                          {new Date(point.day.date).getDate()}
                        </text>
                      )}
                    </g>
                  ))}
                </svg>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-green-50 dark:bg-green-900/20 p-3">
                  <p className="text-[11px] uppercase text-green-600 dark:text-green-400 font-medium">Best Day</p>
                  <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                    {bestSuccessDay ? formatDate(bestSuccessDay.date) : 'ไม่มีข้อมูล'}
                  </p>
                  <p className={`text-xs mt-1 ${bestSuccessDay ? getSuccessRateTextColor(bestSuccessDay.success_rate) : 'text-gray-400 dark:text-slate-500'}`}>
                    {bestSuccessDay ? `${bestSuccessDay.success_rate.toFixed(1)}%` : '-'}
                  </p>
                </div>
                <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
                  <p className="text-[11px] uppercase text-amber-600 dark:text-amber-400 font-medium">Lowest Day</p>
                  <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                    {weakestSuccessDay ? formatDate(weakestSuccessDay.date) : 'ไม่มีข้อมูล'}
                  </p>
                  <p className={`text-xs mt-1 ${weakestSuccessDay ? getSuccessRateTextColor(weakestSuccessDay.success_rate) : 'text-gray-400 dark:text-slate-500'}`}>
                    {weakestSuccessDay ? `${weakestSuccessDay.success_rate.toFixed(1)}%` : '-'}
                  </p>
                </div>
                <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
                  <p className="text-[11px] uppercase text-blue-500 dark:text-blue-400 font-medium">Performance Snapshot</p>
                  <p className="text-sm font-bold text-gray-800 dark:text-slate-100 mt-1">
                    {enrichedDays.length.toLocaleString()} วันในรายงาน
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Paid {orderStatusItems[0].value.toLocaleString()} จาก {totalStatusOrders.toLocaleString()} Orders
                  </p>
                </div>
                <div className="rounded-xl p-3" style={{ background: 'var(--theme-primary-light)' }}>
                  <p className="text-[11px] uppercase font-medium" style={{ color: 'var(--theme-primary)' }}>
                    Commission{!isSubReseller && subSpread.total.total_amount > 0 ? ' (+ Sub)' : ''}
                  </p>
                  <p className="text-sm font-bold mt-1 tabular-nums" style={{ color: 'var(--theme-primary)' }}>+{formatCurrency(totalMonthCommission)}</p>
                  {!isSubReseller && subSpread.total.total_amount > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 tabular-nums">Sub ฿{fmtMoney(subSpread.total.total_amount)}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Daily Report Table */}
      <DataTable
        data={enrichedDays}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีข้อมูลในเดือนนี้"
        emptyIcon={<FontAwesomeIcon icon={faChartPie} className="text-5xl text-gray-200 dark:text-slate-600" />}
        pagination={{ pageSize: 31, showPageSizeSelector: false }}
        headerContent={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-lg" style={{ color: 'var(--theme-primary)' }} />
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">
              รายงานรายวัน - {THAI_MONTHS[month - 1]} {year}
              {selectedMerchantName && (
                <span className="text-sm font-normal text-gray-500 dark:text-slate-400 ml-0 sm:ml-2 inline-flex items-center">
                  <FontAwesomeIcon icon={faBuilding} className="mr-1" />
                  {selectedMerchantName}
                </span>
              )}
            </h2>
          </div>
        }
        hoverable
      />
    </div>
  );
};

export default ResellerMonthlyReportsPage;
